import './style.scss';
/* eslint-disable no-unneeded-ternary */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import getElectron from 'app/native/node/electron';
import { Button, Dimmer, Icon, Loader, Message, Popup } from 'semantic-ui-react';
import exportStudy, {
  ExportableStudy,
  JPEGFormat,
} from 'app/components/Dropzone/ExportStudyModal/exportStudy';
import { SelectableImage } from 'app/components/ImagesSelector';
import { DicomData } from 'app/interfaces/Dicom';
import IDisplayableImageEncoderContext from 'app/providers/IDisplayableImageEncoder/context';
import ImagesExportSelector from 'app/components/Dropzone/ExportStudyModal/ImagesExportSelector';
import ModalWithSideImagePreview, {
  useImagePreview,
} from 'app/components/ModalWithSideImagePreview';
import { selectFileExportSettings, setFileExportAnnotations } from 'app/redux/exportSettings';
import { ComputeDicomOptions } from 'app/components/Viewer/types';

export const LOCALSTORAGE_EXPORT_STUDY_MODAL = 'component.ExportStudyModal';

export const defaultInitValues = {
  isReportIncluded: true,
  jpgFormat: {
    isSelected: true,
    quality: 95,
    maxDimension: 1920,
  },
  dicomFormat: {
    isSelected: true,
  },
  thumbnailFormat: {
    isSelected: true,
    quality: 95,
    maxDimension: 1080,
  },
  directoryPath: '',
};

function ExportButton({ setIsExportModalOpen }: { setIsExportModalOpen: (open: boolean) => void }) {
  return (
    <Popup
      trigger={
        <button
          className="picoxia"
          type="button"
          data-testid="modal-trigger"
          onClick={() => setIsExportModalOpen(true)}
        >
          <Icon name="download" size="big" />
        </button>
      }
      content={<FormattedMessage id="dropzone.export.tooltip" />}
    />
  );
}

export type ImageWithDicomComputation = {
  computeDicomData?: (options?: ComputeDicomOptions) => Promise<DicomData>;
};

export type ExportStudyModalProps = {
  currentStudy: {
    images: (SelectableImage & ImageWithDicomComputation & ExportableStudy['images'][number])[];
  };
} & { currentStudy: ExportableStudy };

type ExportFormContentProps = {
  images: ExportStudyModalProps['currentStudy']['images'];
  dicomFormat: { isSelected: boolean };
  isReportIncluded: boolean;
  isExportAllAnnotations: boolean;
  setIsExportAllAnnotations: (checked: boolean) => void;
  toggleIsReportIncluded: () => void;
  toggleIsDicomFormatIncluded: () => void;
  toggleIsThumbnailFormatIncluded: () => void;
  toggleIsJPGFormatIncluded: () => void;
  thumbnailFormat: { isSelected: boolean };
  jpgFormat: { isSelected: boolean; quality: number; maxDimension: number };
  updateJPGFormatParameter: (parameter: keyof JPEGFormat, value: number) => void;
  directoryPath: string;
  setDirectoryPath: (path: string) => void;
  errorMessage: string | null;
  onImagesSelect: (imageIds: (number | string)[]) => void;
};

function ExportFormContent({
  images,
  dicomFormat,
  isReportIncluded,
  isExportAllAnnotations,
  setIsExportAllAnnotations,
  toggleIsReportIncluded,
  toggleIsDicomFormatIncluded,
  toggleIsThumbnailFormatIncluded,
  toggleIsJPGFormatIncluded,
  thumbnailFormat,
  jpgFormat,
  updateJPGFormatParameter,
  directoryPath,
  setDirectoryPath,
  errorMessage,
  onImagesSelect,
}: ExportFormContentProps) {
  const intl = useIntl();
  const { openPreview } = useImagePreview();
  const dialog = getElectron()?.remote?.dialog;

  const handleImageClick = (imageId: number | string) => {
    const imageIndex = images.findIndex((img) => img.id === imageId);
    if (imageIndex !== -1) {
      openPreview(imageIndex);
    }
  };

  const chooseDirectory = () => {
    dialog?.showOpenDialog({ properties: ['openDirectory'] }).then((selectedDirectories) => {
      if (selectedDirectories.canceled) return;
      setDirectoryPath(selectedDirectories.filePaths[0]);
    });
  };

  return (
    <form className="exportForm">
      <fieldset>
        <legend>
          <FormattedMessage id="exportStudyModal.selectImages.label" />
        </legend>
        <ImagesExportSelector
          onSelect={onImagesSelect}
          images={images}
          isDicomExport={dicomFormat.isSelected}
          onImageClick={handleImageClick}
        />
        <div className="checkbox">
          <input
            type="checkbox"
            id="export-annotations"
            name="export-annotations"
            data-testid="export-annotations"
            checked={isExportAllAnnotations}
            onChange={() => setIsExportAllAnnotations(!isExportAllAnnotations)}
          />
          <label htmlFor="export-annotations">
            <FormattedMessage id="general.export.includeAnnotations" />
          </label>
        </div>
      </fieldset>

      <fieldset>
        <legend>
          <FormattedMessage id="exportStudyModal.configSection.label" />
        </legend>
        <div className="includeReport config-section">
          <span className="label">
            <FormattedMessage id="exportStudyModal.report.label" />
          </span>
          <div className="checkbox">
            <input
              data-testid="report-checkbox"
              type="checkbox"
              id="include-report"
              name="include-report"
              checked={isReportIncluded}
              onChange={toggleIsReportIncluded}
            />
            <label htmlFor="include-report">
              <FormattedMessage id="exportStudyModal.report.option.include" />
            </label>
          </div>
        </div>

        <div className="section chooseFormats config-section">
          <span className="label">
            <FormattedMessage id="exportStudyModal.formats.label" />
          </span>
          <div className="checkbox">
            <input
              type="checkbox"
              id="dicom"
              name="dicom"
              data-testid="format-dicom"
              checked={dicomFormat.isSelected}
              onChange={toggleIsDicomFormatIncluded}
            />
            <label htmlFor="dicom">
              <FormattedMessage id="exportStudyModal.formats.option.dicom" />
            </label>
          </div>
          <div className="checkbox">
            <input
              type="checkbox"
              id="thumbnail"
              name="thumbnail"
              data-testid="format-thumbnail"
              checked={thumbnailFormat.isSelected}
              onChange={toggleIsThumbnailFormatIncluded}
            />
            <label htmlFor="thumbnail">
              <FormattedMessage id="exportStudyModal.formats.option.thumbnail" />
            </label>
          </div>

          <div>
            <div className="checkbox">
              <input
                type="checkbox"
                id="jpg"
                name="jpg"
                data-testid="format-jpg"
                checked={jpgFormat.isSelected}
                onChange={toggleIsJPGFormatIncluded}
              />
              <label htmlFor="jpg">
                <FormattedMessage id="exportStudyModal.formats.option.jpg" />
              </label>
            </div>
            {jpgFormat.isSelected && (
              <div className="chooseFormats__jpgParameters">
                <svg
                  className="chooseFormats__jpgParameters-pointer"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 100 100"
                >
                  <polygon points="50 15, 100 100, 0 100" />
                </svg>
                <div className="chooseFormats__parameter">
                  <label className="chooseFormats__parameterLabel" htmlFor="quality">
                    <FormattedMessage id="exportStudyModal.formats.jpg.quality" />
                  </label>
                  <input
                    data-testid="format-jpg-quality"
                    style={{ backgroundSize: `${jpgFormat.quality}% 100%` }}
                    type="range"
                    step="1"
                    value={jpgFormat.quality}
                    min="0"
                    max="100"
                    id="quality"
                    name="quality"
                    onChange={(event) => {
                      updateJPGFormatParameter('quality', parseInt(event.target.value));
                    }}
                  />
                  <p>{jpgFormat.quality} %</p>
                </div>
                <div className="chooseFormats__parameter">
                  <label className="chooseFormats__parameterLabel" htmlFor="maxDimension">
                    <FormattedMessage id="exportStudyModal.formats.jpg.maxDimension" />
                  </label>
                  <input
                    data-testid="format-jpg-maxdim"
                    className="chooseFormats__parameterInput"
                    type="number"
                    id="maxDimension"
                    name="maxDimension"
                    step="10"
                    value={jpgFormat.maxDimension}
                    onChange={(event) => {
                      updateJPGFormatParameter('maxDimension', parseInt(event.target.value));
                    }}
                  />
                  <span style={{ marginLeft: '-20px' }}>px</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {process.env.PLATFORM === 'electron' && (
          <div className="section directoryPath">
            <label className="label">
              <FormattedMessage id="exportStudyModal.directoryPath.label" />
            </label>
            <input
              data-testid="directory-path-input"
              type="text"
              className="directoryPath__input"
              value={directoryPath}
              readOnly
            />
            <button
              data-testid="directory-path-button"
              type="button"
              className="directoryPath__chooseButton"
              onClick={chooseDirectory}
            >
              <FormattedMessage id="exportStudyModal.directoryPath.button.choose" />
            </button>
          </div>
        )}
      </fieldset>

      {errorMessage && (
        <Message
          data-testid="error-message"
          error
          header={intl.formatMessage({ id: 'exportStudyModal.errorMessage.header' })}
          content={intl.formatMessage({
            id: `exportStudyModal.errorMessage.${errorMessage}`,
          })}
        />
      )}
    </form>
  );
}

function ExportStudyModal({ currentStudy }: ExportStudyModalProps) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const imageEncoder = useContext(IDisplayableImageEncoderContext);
  const { animal, images, comment, creationDate } = currentStudy;

  const fileExportSettings = useSelector(selectFileExportSettings);
  const isExportAllAnnotations = fileExportSettings.isExportAllAnnotations;
  const setIsExportAllAnnotations = (checked: boolean) => {
    dispatch(setFileExportAnnotations(checked));
  };

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportOngoing, setIsExportOngoing] = useState(false);
  const [selectedImages, setSelectedImages] = useState<(number | string)[]>([]);

  const onImagesSelect = (imageIds: (number | string)[]) => {
    setSelectedImages(imageIds);
    setErrorMessage(null);
  };

  const getExportValuesStoredIntoLocalStorage = () =>
    JSON.parse(localStorage.getItem(LOCALSTORAGE_EXPORT_STUDY_MODAL));

  const initIsReportIncludedValue = () =>
    getExportValuesStoredIntoLocalStorage()?.isReportIncluded ?? defaultInitValues.isReportIncluded;
  const [isReportIncluded, setIsReportIncluded] = useState(initIsReportIncludedValue);

  const initJPGFormatValues = () => {
    const jpgFormatFromLocalStorage = getExportValuesStoredIntoLocalStorage()?.jpgFormat;
    return {
      isSelected: jpgFormatFromLocalStorage?.isSelected ?? defaultInitValues.jpgFormat.isSelected,
      quality: jpgFormatFromLocalStorage?.quality ?? defaultInitValues.jpgFormat.quality,
      maxDimension:
        jpgFormatFromLocalStorage?.maxDimension ?? defaultInitValues.jpgFormat.maxDimension,
    };
  };
  const [jpgFormat, setJpgFormat] = useState(initJPGFormatValues);

  const initDicomFormatValues = () => {
    const dicomFormatFromLocalStorage = getExportValuesStoredIntoLocalStorage()?.dicomFormat;
    return {
      isSelected:
        dicomFormatFromLocalStorage?.isSelected ?? defaultInitValues.dicomFormat.isSelected,
    };
  };
  const [dicomFormat, setDicomFormat] = useState(initDicomFormatValues);

  const initThumbnailFormatValues = () => {
    const thumbnailFormatFromLocalStorage =
      getExportValuesStoredIntoLocalStorage()?.thumbnailFormat;
    return {
      isSelected:
        thumbnailFormatFromLocalStorage?.isSelected ?? defaultInitValues.thumbnailFormat.isSelected,
      quality: defaultInitValues.thumbnailFormat.quality,
      maxDimension: defaultInitValues.thumbnailFormat.maxDimension,
    };
  };
  const [thumbnailFormat, setThumbnailFormat] = useState(initThumbnailFormatValues);

  const initDirectoryPathValue = () =>
    getExportValuesStoredIntoLocalStorage()?.directoryPath ?? defaultInitValues.directoryPath;
  const [directoryPath, setDirectoryPath] = useState(initDirectoryPathValue);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const exportDataToStore = {
      isReportIncluded,
      jpgFormat,
      dicomFormat,
      thumbnailFormat,
      directoryPath,
    };
    localStorage.setItem(LOCALSTORAGE_EXPORT_STUDY_MODAL, JSON.stringify(exportDataToStore));
  }, [isReportIncluded, jpgFormat, dicomFormat, thumbnailFormat, directoryPath]);

  const toggleIsReportIncluded = () => {
    setIsReportIncluded(!isReportIncluded);
  };

  const toggleIsJPGFormatIncluded = () => {
    setJpgFormat({ ...jpgFormat, isSelected: !jpgFormat.isSelected });
    setErrorMessage(null);
  };

  const updateJPGFormatParameter = (
    parameter: keyof JPEGFormat,
    value: JPEGFormat[keyof JPEGFormat]
  ) => {
    setJpgFormat({ ...jpgFormat, [parameter]: value });
  };

  const toggleIsDicomFormatIncluded = () => {
    setDicomFormat({ ...dicomFormat, isSelected: !dicomFormat.isSelected });
    setErrorMessage(null);
  };

  const toggleIsThumbnailFormatIncluded = () => {
    setThumbnailFormat({ ...thumbnailFormat, isSelected: !thumbnailFormat.isSelected });
    setErrorMessage(null);
  };

  const handleSetDirectoryPath = (path: string) => {
    setDirectoryPath(path);
    setErrorMessage(null);
  };

  const checkIfErrors = () => {
    if (selectedImages.length === 0) {
      setErrorMessage('noImagesSelected');
      return true;
    }
    if (process.env.PLATFORM === 'electron' && directoryPath === '') {
      setErrorMessage('noDirectoryPath');
      return true;
    }
    if (!jpgFormat.isSelected && !dicomFormat.isSelected && !thumbnailFormat.isSelected) {
      setErrorMessage('noFormatsSelected');
      return true;
    }
    return false;
  };

  const handleExportStudy = async () => {
    if (checkIfErrors()) return;

    setErrorMessage(null);

    const exportParameters = {
      selectedImages,
      isReportIncluded,
      jpgFormat,
      dicomFormat,
      thumbnailFormat,
      directoryPath,
    };

    setIsExportOngoing(true);
    try {
      let imagesToExport = images.filter((image) => selectedImages.includes(image.id));
      if (dicomFormat.isSelected) {
        imagesToExport = await Promise.all(
          imagesToExport.map(async (image) => ({
            ...image,
            dicomData: await image.computeDicomData?.({
              includeAnnotations: isExportAllAnnotations,
            }),
          }))
        );
      }

      await exportStudy(
        intl,
        imageEncoder,
        { animal, images: imagesToExport, comment, creationDate },
        exportParameters
      );
      setIsExportModalOpen(false);
    } catch (error) {
      setErrorMessage('exportError');
      console.error(error);
    }
    setIsExportOngoing(false);
  };

  return (
    <>
      <ExportButton setIsExportModalOpen={setIsExportModalOpen} />
      <ModalWithSideImagePreview
        className="dark-mode"
        previewableImages={currentStudy.images}
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onOpen={() => setIsExportModalOpen(true)}
        mainCardClassName="exportModal"
        previewCardClassName="exportModal"
      >
        <ModalWithSideImagePreview.Header>
          <FormattedMessage id="exportStudyModal.title" />
        </ModalWithSideImagePreview.Header>
        <ModalWithSideImagePreview.Content data-testid="modal-content">
          <ExportFormContent
            images={images}
            dicomFormat={dicomFormat}
            isReportIncluded={isReportIncluded}
            isExportAllAnnotations={isExportAllAnnotations}
            setIsExportAllAnnotations={setIsExportAllAnnotations}
            toggleIsReportIncluded={toggleIsReportIncluded}
            toggleIsDicomFormatIncluded={toggleIsDicomFormatIncluded}
            toggleIsThumbnailFormatIncluded={toggleIsThumbnailFormatIncluded}
            toggleIsJPGFormatIncluded={toggleIsJPGFormatIncluded}
            thumbnailFormat={thumbnailFormat}
            jpgFormat={jpgFormat}
            updateJPGFormatParameter={updateJPGFormatParameter}
            directoryPath={directoryPath}
            setDirectoryPath={handleSetDirectoryPath}
            errorMessage={errorMessage}
            onImagesSelect={onImagesSelect}
          />
        </ModalWithSideImagePreview.Content>
        <ModalWithSideImagePreview.Actions>
          <Button
            data-testid="button-export"
            type="button"
            positive
            className="footerModal__button footerModal__button--green"
            onClick={handleExportStudy}
          >
            <Dimmer.Dimmable>
              <Dimmer data-testid="export-ongoing" active={isExportOngoing}>
                <Loader />
              </Dimmer>
            </Dimmer.Dimmable>
            <FormattedMessage id="exportStudyModal.action.button.export" />
          </Button>
          <Button
            type="button"
            secondary
            className="footerModal__button"
            onClick={() => setIsExportModalOpen(false)}
          >
            <FormattedMessage id="exportStudyModal.action.button.cancel" />
          </Button>
        </ModalWithSideImagePreview.Actions>
      </ModalWithSideImagePreview>
    </>
  );
}

export default ExportStudyModal;
